import type { AzureSpeechSettingsPayload } from '@Application/Ai/AiConfiguration/Models/Actions/aiModelsFetched';
import type AiModelView from '@UI/Store/AiConfiguration/Views/AiModelView';
import isAzureSpeechChunkMaxSecondsValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isAzureSpeechChunkMaxSecondsValid';
import isSpeechLocaleValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isSpeechLocaleValid';
import isSpeechMaxSpeakersValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isSpeechMaxSpeakersValid';
import isSpeechPhraseWeightValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isSpeechPhraseWeightValid';
import isSpeechPhraseLengthValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isSpeechPhraseLengthValid';
import isSpeechPhrasesCountValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isSpeechPhrasesCountValid';
import normalizeSpeechPhrases from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/Helpers/normalizeSpeechPhrases';
import isChunkMaxSecondsValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isChunkMaxSecondsValid';
import isChunkingRequiredForRealtime from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isChunkingRequiredForRealtime';
import isConcurrencyValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isConcurrencyValid';
import isLiveUsageRealtime from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isLiveUsageRealtime';
import isDeliveryModeRequiredForAudioUsages from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isDeliveryModeRequiredForAudioUsages';
import isFamilyRequiredForAudioUsages from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isFamilyRequiredForAudioUsages';
import isRealtimeDeliveryCompatibleWithFamily from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isRealtimeDeliveryCompatibleWithFamily';
import isPriceValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isPriceValid';
import isProviderOptionsJsonValid from '@UI/Pages/Administration/AiEngines/Sidebars/ModelSidebar/FormData/constraint/isProviderOptionsJsonValid';
import AUDIO_USAGES from '@UI/Pages/Administration/AiEngines/audioUsages';
import { AZURE_SPEECH_FAMILY } from '@UI/Pages/Administration/AiEngines/azureSpeechValues';
import { FILE_DELIVERY_MODE } from '@UI/Pages/Administration/AiEngines/realtimeDeliveryRules';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type AiModelFormArgs = {
	providerId: number | null;
	identifier: string;
	label: string;
	usages: string[];
	apiVersion: string;
	family: string | null;
	deliveryMode: string | null;
	providerOptions: string;
	transcriptionChunking: boolean;
	transcriptionChunkMaxSeconds: string;
	transcriptionConcurrency: string;
	inputPricePerMillionTokens: string;
	outputPricePerMillionTokens: string;
	cachedInputPricePerMillionTokens: string;
	cacheWritePricePerMillionTokens: string;
	pricePerAudioMinute: string;
	billedPerStartedMinute: boolean;
	hasAzureSpeechSettings: boolean;
	speechLocale: string;
	speechMaxSpeakers: string;
	speechProfanityFilter: boolean;
	speechPhrases: string;
	speechPhraseWeight: string;
};

export type AiModelCommandData = {
	readonly identifier: string;
	readonly label: string;
	readonly usages: string[];
	readonly apiVersion: string | null;
	readonly family: string | null;
	readonly deliveryMode: string | null;
	readonly providerOptions: Record<string, unknown> | null;
	readonly transcriptionChunking: boolean;
	readonly transcriptionChunkMaxSeconds: number | null;
	readonly transcriptionConcurrency: number | null;
	readonly inputPricePerMillionTokens: number | null;
	readonly outputPricePerMillionTokens: number | null;
	readonly cachedInputPricePerMillionTokens: number | null;
	readonly cacheWritePricePerMillionTokens: number | null;
	readonly pricePerAudioMinute: number | null;
	readonly billedPerStartedMinute: boolean;
	readonly azureSpeechSettings: AzureSpeechSettingsPayload | null;
};

/**
 * `apiVersion`, `usages` and the base fields express with Joi alone (required, max length,
 * non-empty array). `family` and `providerOptions` need a custom constraint each — conditional
 * requiredness and JSON shape are not expressible in Joi — hence `FormData/constraint/`.
 * `apiVersion` is always facultative — unlike the provider's own version, it is a model-level
 * override, never required regardless of the selected provider's kind: an empty value simply means
 * "use the provider's version" (`toCommandData`).
 *
 * `deliveryMode` is validated by three constraints: required for an audio model, `realtime` only for a
 * family some provider serves in real time, and forced to `realtime` by a live usage. The provider kind
 * is not checked here, the server being the authority on it.
 *
 * `transcriptionChunkMaxSeconds` and `transcriptionConcurrency` are strings so the field can be empty,
 * meaning "the server's own value"; `toCommandData` turns the empty string into `null`.
 *
 * The four prices are strings for the same reason: empty means "not set", turned into `null` by
 * `toCommandData`.
 *
 * The `speech*` fields mirror the Azure Speech settings the view carries; `hasAzureSpeechSettings` is true
 * only when the edited model already has them (a model being created gets the server's defaults), and the
 * five custom constraints on them are inactive otherwise. `speechPhrases` holds one phrase per line;
 * `speechPhraseWeight` empty means "Microsoft's own weight".
 *
 * Deliberately **not** `ignoreNullValue`: with it, a `null` `family` would be reputed valid and
 * `isFamilyRequiredForAudioUsages` would never run — exactly the case it exists to catch (an audio
 * model declared with no family).
 */
const toPrice = (value: string): number | null => (value === '' ? null : Number(value));

const PRICE_MESSAGE = 'Saisissez un prix positif, avec au plus 6 décimales.';

@ValidatedClass()
class AiModelFormData extends AbstractValidatedClass<AiModelFormArgs> {
	@constraint(Joi.number().required(), () => 'Choisissez un fournisseur.')
	public readonly providerId: number | null;

	@constraint(Joi.string().max(200), () => 'L’identifiant ne peut pas dépasser 200 caractères.')
	@constraint(Joi.string().required(), () => 'L’identifiant du modèle est obligatoire.')
	public readonly identifier: string;

	@constraint(Joi.string().max(120), () => 'Le nom ne peut pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom du modèle est obligatoire.')
	public readonly label: string;

	@constraint(Joi.array().items(Joi.string()).min(1).required(), () => 'Déclarez au moins un usage.')
	public readonly usages: string[];

	@constraint(Joi.string().max(40).allow(''), () => 'La version d’API ne peut pas dépasser 40 caractères.')
	public readonly apiVersion: string;

	@constraint(
		isFamilyRequiredForAudioUsages,
		() => 'Ce modèle porte un usage audio : sa famille est requise.',
	)
	public readonly family: string | null;

	@constraint(isLiveUsageRealtime, () => 'Un usage en direct impose le mode d’envoi Temps réel.')
	@constraint(
		isRealtimeDeliveryCompatibleWithFamily,
		() => 'Le temps réel n’est proposé que pour les familles Voxtral et OpenAI Transcribe.',
	)
	@constraint(
		isDeliveryModeRequiredForAudioUsages,
		() => 'Ce modèle porte un usage audio : son mode d’envoi est requis.',
	)
	public readonly deliveryMode: string | null;

	@constraint(isProviderOptionsJsonValid, () => 'Les options doivent être un objet JSON valide.')
	public readonly providerOptions: string;

	@constraint(
		isChunkingRequiredForRealtime,
		() => 'Le mode Temps réel impose de découper l’audio en morceaux.',
	)
	public readonly transcriptionChunking: boolean;

	@constraint(
		isAzureSpeechChunkMaxSecondsValid,
		() =>
			'Pour Azure Speech, la durée d’un morceau doit être un nombre entier de secondes, de 60 à 7 200.',
	)
	@constraint(
		isChunkMaxSecondsValid,
		() => 'La durée d’un morceau doit être un nombre entier de secondes, de 60 à 1200.',
	)
	public readonly transcriptionChunkMaxSeconds: string;

	@constraint(isConcurrencyValid, () => 'Le nombre d’appels en parallèle doit être un entier de 1 à 10.')
	public readonly transcriptionConcurrency: string;

	@constraint(isPriceValid, () => PRICE_MESSAGE)
	public readonly inputPricePerMillionTokens: string;

	@constraint(isPriceValid, () => PRICE_MESSAGE)
	public readonly outputPricePerMillionTokens: string;

	@constraint(isPriceValid, () => PRICE_MESSAGE)
	public readonly cachedInputPricePerMillionTokens: string;

	@constraint(isPriceValid, () => PRICE_MESSAGE)
	public readonly cacheWritePricePerMillionTokens: string;

	@constraint(isPriceValid, () => PRICE_MESSAGE)
	public readonly pricePerAudioMinute: string;

	public readonly billedPerStartedMinute: boolean;

	public readonly hasAzureSpeechSettings: boolean;

	@constraint(isSpeechLocaleValid, () => 'La langue doit être un code de la forme fr-FR.')
	public readonly speechLocale: string;

	@constraint(isSpeechMaxSpeakersValid, () => 'Le nombre maximal de locuteurs va de 2 à 35.')
	public readonly speechMaxSpeakers: string;

	public readonly speechProfanityFilter: boolean;

	@constraint(isSpeechPhraseLengthValid, () => 'Une phrase ne peut pas dépasser 200 caractères.')
	@constraint(isSpeechPhrasesCountValid, () => 'La liste ne peut pas dépasser 2000 phrases.')
	public readonly speechPhrases: string;

	@constraint(isSpeechPhraseWeightValid, () => 'Le poids de la liste va de 0 à 2.')
	public readonly speechPhraseWeight: string;

	constructor(data: AiModelFormArgs) {
		super(data);
		this.providerId = data.providerId;
		this.identifier = data.identifier;
		this.label = data.label;
		this.usages = data.usages;
		this.apiVersion = data.apiVersion;
		this.family = data.family;
		this.deliveryMode = data.deliveryMode;
		this.providerOptions = data.providerOptions;
		this.transcriptionChunking = data.transcriptionChunking;
		this.transcriptionChunkMaxSeconds = data.transcriptionChunkMaxSeconds;
		this.transcriptionConcurrency = data.transcriptionConcurrency;
		this.inputPricePerMillionTokens = data.inputPricePerMillionTokens;
		this.outputPricePerMillionTokens = data.outputPricePerMillionTokens;
		this.cachedInputPricePerMillionTokens = data.cachedInputPricePerMillionTokens;
		this.cacheWritePricePerMillionTokens = data.cacheWritePricePerMillionTokens;
		this.pricePerAudioMinute = data.pricePerAudioMinute;
		this.billedPerStartedMinute = data.billedPerStartedMinute;
		this.hasAzureSpeechSettings = data.hasAzureSpeechSettings;
		this.speechLocale = data.speechLocale;
		this.speechMaxSpeakers = data.speechMaxSpeakers;
		this.speechProfanityFilter = data.speechProfanityFilter;
		this.speechPhrases = data.speechPhrases;
		this.speechPhraseWeight = data.speechPhraseWeight;
		Object.freeze(this);
	}

	static createEmpty(providerId: number | null = null): AiModelFormData {
		return new AiModelFormData({
			providerId,
			identifier: '',
			label: '',
			usages: [],
			apiVersion: '',
			family: null,
			deliveryMode: FILE_DELIVERY_MODE,
			providerOptions: '',
			transcriptionChunking: true,
			transcriptionChunkMaxSeconds: '',
			transcriptionConcurrency: '',
			inputPricePerMillionTokens: '',
			outputPricePerMillionTokens: '',
			cachedInputPricePerMillionTokens: '',
			cacheWritePricePerMillionTokens: '',
			pricePerAudioMinute: '',
			billedPerStartedMinute: false,
			hasAzureSpeechSettings: false,
			speechLocale: '',
			speechMaxSpeakers: '',
			speechProfanityFilter: false,
			speechPhrases: '',
			speechPhraseWeight: '',
		});
	}

	static fromView(model: AiModelView): AiModelFormData {
		return new AiModelFormData({
			providerId: model.providerId,
			identifier: model.identifier,
			label: model.label,
			usages: [...model.usages],
			apiVersion: model.apiVersion ?? '',
			family: model.family?.value ?? null,
			deliveryMode: model.deliveryMode?.value ?? FILE_DELIVERY_MODE,
			providerOptions:
				model.providerOptions === null ? '' : JSON.stringify(model.providerOptions, null, 2),
			transcriptionChunking: model.transcriptionChunking,
			transcriptionChunkMaxSeconds: model.transcriptionChunkMaxSeconds?.toString() ?? '',
			transcriptionConcurrency: model.transcriptionConcurrency?.toString() ?? '',
			inputPricePerMillionTokens: model.inputPricePerMillionTokens?.toString() ?? '',
			outputPricePerMillionTokens: model.outputPricePerMillionTokens?.toString() ?? '',
			cachedInputPricePerMillionTokens: model.cachedInputPricePerMillionTokens?.toString() ?? '',
			cacheWritePricePerMillionTokens: model.cacheWritePricePerMillionTokens?.toString() ?? '',
			pricePerAudioMinute: model.pricePerAudioMinute?.toString() ?? '',
			billedPerStartedMinute: model.billedPerStartedMinute,
			hasAzureSpeechSettings: model.azureSpeechSettings !== null,
			speechLocale: model.azureSpeechSettings?.locale ?? '',
			speechMaxSpeakers: model.azureSpeechSettings?.maxSpeakers.toString() ?? '',
			speechProfanityFilter: model.azureSpeechSettings?.profanityFilter ?? false,
			speechPhrases: model.azureSpeechSettings?.phrases.join('\n') ?? '',
			speechPhraseWeight: model.azureSpeechSettings?.phraseWeight?.toString() ?? '',
		});
	}

	setProviderId(providerId: number | null): this {
		return this.cloneWith({ providerId } as Partial<AiModelFormArgs>);
	}

	setIdentifier(identifier: string): this {
		return this.cloneWith({ identifier } as Partial<AiModelFormArgs>);
	}

	setLabel(label: string): this {
		return this.cloneWith({ label } as Partial<AiModelFormArgs>);
	}

	setUsages(usages: string[]): this {
		return this.cloneWith({ usages } as Partial<AiModelFormArgs>);
	}

	setApiVersion(apiVersion: string): this {
		return this.cloneWith({ apiVersion } as Partial<AiModelFormArgs>);
	}

	setFamily(family: string | null): this {
		return this.cloneWith({ family } as Partial<AiModelFormArgs>);
	}

	setDeliveryMode(deliveryMode: string | null): this {
		return this.cloneWith({ deliveryMode } as Partial<AiModelFormArgs>);
	}

	setProviderOptions(providerOptions: string): this {
		return this.cloneWith({ providerOptions } as Partial<AiModelFormArgs>);
	}

	setTranscriptionChunking(transcriptionChunking: boolean): this {
		return this.cloneWith({ transcriptionChunking } as Partial<AiModelFormArgs>);
	}

	setTranscriptionChunkMaxSeconds(transcriptionChunkMaxSeconds: string): this {
		return this.cloneWith({ transcriptionChunkMaxSeconds } as Partial<AiModelFormArgs>);
	}

	setTranscriptionConcurrency(transcriptionConcurrency: string): this {
		return this.cloneWith({ transcriptionConcurrency } as Partial<AiModelFormArgs>);
	}

	setInputPricePerMillionTokens(inputPricePerMillionTokens: string): this {
		return this.cloneWith({ inputPricePerMillionTokens } as Partial<AiModelFormArgs>);
	}

	setOutputPricePerMillionTokens(outputPricePerMillionTokens: string): this {
		return this.cloneWith({ outputPricePerMillionTokens } as Partial<AiModelFormArgs>);
	}

	setCachedInputPricePerMillionTokens(cachedInputPricePerMillionTokens: string): this {
		return this.cloneWith({ cachedInputPricePerMillionTokens } as Partial<AiModelFormArgs>);
	}

	setCacheWritePricePerMillionTokens(cacheWritePricePerMillionTokens: string): this {
		return this.cloneWith({ cacheWritePricePerMillionTokens } as Partial<AiModelFormArgs>);
	}

	setPricePerAudioMinute(pricePerAudioMinute: string): this {
		return this.cloneWith({ pricePerAudioMinute } as Partial<AiModelFormArgs>);
	}

	setBilledPerStartedMinute(billedPerStartedMinute: boolean): this {
		return this.cloneWith({ billedPerStartedMinute } as Partial<AiModelFormArgs>);
	}

	setSpeechLocale(speechLocale: string): this {
		return this.cloneWith({ speechLocale } as Partial<AiModelFormArgs>);
	}

	setSpeechMaxSpeakers(speechMaxSpeakers: string): this {
		return this.cloneWith({ speechMaxSpeakers } as Partial<AiModelFormArgs>);
	}

	setSpeechProfanityFilter(speechProfanityFilter: boolean): this {
		return this.cloneWith({ speechProfanityFilter } as Partial<AiModelFormArgs>);
	}

	setSpeechPhrases(speechPhrases: string): this {
		return this.cloneWith({ speechPhrases } as Partial<AiModelFormArgs>);
	}

	setSpeechPhraseWeight(speechPhraseWeight: string): this {
		return this.cloneWith({ speechPhraseWeight } as Partial<AiModelFormArgs>);
	}

	/** Produces the fields shared by `CreateAiModel` and `UpdateAiModel`. `providerId` is deliberately
	 * excluded: `UpdateAiModel` never carries it, a model never changing provider once declared.
	 * `apiVersion` collapses `''` to `null`: an emptied field must repart as "no override", never as an
	 * empty-string override. `family` collapses `''` to `null` the same way. `deliveryMode` is `null` unless the model carries an
	 * audio usage: a text model never sends one. `providerOptions` collapses
	 * `''` to `null`, which erases the registered settings: the field is filled from the stored value,
	 * so an emptied field is a deliberate removal, not an absence of input. The two chunking numbers collapse
	 * `''` to `null` ("the server's own value"), never to `0`. The four prices collapse `''` to `null` too: "not set" is never zero.
	 * `azureSpeechSettings` is `null` unless the Azure Speech section is active; its phrases are normalized
	 * and an empty weight collapses to `null`. */
	toCommandData(): AiModelCommandData {
		const hasAudioUsage = this.usages.some((usage) => AUDIO_USAGES.includes(usage));

		return {
			identifier: this.identifier,
			label: this.label,
			usages: this.usages,
			apiVersion: this.apiVersion === '' ? null : this.apiVersion,
			family: this.family === '' ? null : this.family,
			deliveryMode: hasAudioUsage ? this.deliveryMode : null,
			providerOptions:
				this.providerOptions === ''
					? null
					: (JSON.parse(this.providerOptions) as Record<string, unknown>),
			transcriptionChunking: this.transcriptionChunking,
			transcriptionChunkMaxSeconds:
				this.transcriptionChunkMaxSeconds === '' ? null : Number(this.transcriptionChunkMaxSeconds),
			transcriptionConcurrency:
				this.transcriptionConcurrency === '' ? null : Number(this.transcriptionConcurrency),
			inputPricePerMillionTokens: toPrice(this.inputPricePerMillionTokens),
			outputPricePerMillionTokens: toPrice(this.outputPricePerMillionTokens),
			cachedInputPricePerMillionTokens: toPrice(this.cachedInputPricePerMillionTokens),
			cacheWritePricePerMillionTokens: toPrice(this.cacheWritePricePerMillionTokens),
			pricePerAudioMinute: toPrice(this.pricePerAudioMinute),
			billedPerStartedMinute: this.billedPerStartedMinute,
			azureSpeechSettings: this.toAzureSpeechSettings(),
		};
	}

	private toAzureSpeechSettings(): AzureSpeechSettingsPayload | null {
		if (this.family !== AZURE_SPEECH_FAMILY || !this.hasAzureSpeechSettings) {
			return null;
		}

		return {
			locale: this.speechLocale.trim(),
			maxSpeakers: Number(this.speechMaxSpeakers),
			profanityFilter: this.speechProfanityFilter,
			phrases: normalizeSpeechPhrases(this.speechPhrases),
			phraseWeight: this.speechPhraseWeight.trim() === '' ? null : Number(this.speechPhraseWeight),
		};
	}
}

export default AiModelFormData;
